import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  type SxProps,
  type Theme,
} from "@mui/material";
import { useMemo, useState } from "react";
import {
  type ApiKeyAdminCreateInput,
  ApiKeyAdminListDocument,
  ApiUserIdFormat,
  WebhookTimestampMode,
  useApiKeyAdminCreateMutation,
} from "../../../generated/graphql";
import { blankToNull } from "../../../utils/string";
import useEventTypeOptions from "../hooks/use-event-type-options";
import { throwOnGraphqlErrors } from "../throw-on-graphql-errors";
import AdvancedApiKeyFields from "./advanced-api-key-fields";

const styles = {
  content: { pt: 1 } satisfies SxProps<Theme>,
};

/**
 * The create-mutation fields this form owns. `sendCompanyLocalTimestamps` is
 * dropped because `webhookTimestampMode` replaced it (TC-1130) and the backend
 * lets the mode win when both arrive; submitting the pair would leave the
 * loser sitting in the payload for a reader to puzzle over.
 */
type ApiKeyFormInput = Omit<
  ApiKeyAdminCreateInput,
  "sendCompanyLocalTimestamps"
>;

/**
 * One form field per create-mutation field, non-nullable. Derived from the
 * mutation input so a field added to `ApiKeyAdminCreateInput` without a blank
 * value in {@link INITIAL_API_KEY_FORM} is a compile error — the reset path
 * can no longer silently carry a toggle over to the next key created.
 */
type ApiKeyForm = {
  [K in keyof ApiKeyFormInput]-?: NonNullable<ApiKeyFormInput[K]>;
};

const INITIAL_API_KEY_FORM: ApiKeyForm = {
  name: "",
  webhookUrl: "",
  enableStaging: false,
  webhookAuthHeaderName: "",
  webhookAuthHeaderValue: "",
  payloadShapes: [],
  eventTypes: [],
  idFormat: ApiUserIdFormat.Uuid,
  customMapping: false,
  emitTerminalHandoffEvents: false,
  emitAppointmentCancelledEvents: false,
  webhookTimestampMode: WebhookTimestampMode.Utc,
};

type CreateApiKeyDialogProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onCreated: (uuid: string) => void;
};

/**
 * Modal form for creating a blank API key. The backend generates `apiKey`
 * and `uuid`; this dialog captures only the user-editable scalar fields.
 */
const CreateApiKeyDialog = ({
  open,
  onClose,
  onCreated,
}: CreateApiKeyDialogProps) => {
  const [form, setForm] = useState<ApiKeyForm>(INITIAL_API_KEY_FORM);
  const [error, setError] = useState<string | null>(null);

  const [createKey, { loading }] = useApiKeyAdminCreateMutation({
    refetchQueries: [ApiKeyAdminListDocument],
  });

  const eventTypeOptions = useEventTypeOptions({
    payloadShapes: form.payloadShapes,
    emitTerminalHandoffEvents: form.emitTerminalHandoffEvents,
    eventTypes: form.eventTypes,
  });
  const hasOutOfScopeEventTypes =
    eventTypeOptions.status === "ready" &&
    eventTypeOptions.outOfScope.length > 0;

  const setField = useMemo(() => {
    const set =
      <K extends keyof ApiKeyForm>(key: K) =>
      (value: ApiKeyForm[K]) => {
        setForm((current) => ({ ...current, [key]: value }));
      };
    return {
      name: set("name"),
      webhookUrl: set("webhookUrl"),
      enableStaging: set("enableStaging"),
      webhookAuthHeaderName: set("webhookAuthHeaderName"),
      webhookAuthHeaderValue: set("webhookAuthHeaderValue"),
      payloadShapes: set("payloadShapes"),
      eventTypes: set("eventTypes"),
      idFormat: set("idFormat"),
      customMapping: set("customMapping"),
      emitTerminalHandoffEvents: set("emitTerminalHandoffEvents"),
      webhookTimestampMode: set("webhookTimestampMode"),
    };
  }, []);

  const reset = () => {
    setForm(INITIAL_API_KEY_FORM);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (form.name.trim().length === 0) {
      setError("Name is required");
      return;
    }
    setError(null);
    try {
      const result = await createKey({
        variables: {
          input: {
            ...form,
            name: form.name.trim(),
            webhookUrl: blankToNull(form.webhookUrl),
            webhookAuthHeaderName: blankToNull(form.webhookAuthHeaderName),
            webhookAuthHeaderValue: blankToNull(form.webhookAuthHeaderValue),
          },
        },
      });
      throwOnGraphqlErrors(result);
      const created = result.data?.apiKeyAdminCreate;
      if (created !== null && created !== undefined) {
        reset();
        onCreated(created.uuid);
      }
    } catch (error_) {
      const message =
        error_ instanceof Error ? error_.message : "Unknown error";
      setError(message);
    }
  };

  return (
    <Dialog fullWidth open={open} maxWidth="sm" onClose={handleClose}>
      <DialogTitle>Create API key</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={styles.content}>
          {error !== null && <Alert severity="error">{error}</Alert>}
          <TextField
            required
            fullWidth
            label="Name"
            value={form.name}
            size="small"
            onChange={(e) => {
              setField.name(e.target.value);
            }}
          />
          <TextField
            fullWidth
            label="Webhook URL"
            value={form.webhookUrl}
            size="small"
            placeholder="https://..."
            onChange={(e) => {
              setField.webhookUrl(e.target.value);
            }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.enableStaging}
                onChange={(e) => {
                  setField.enableStaging(e.target.checked);
                }}
              />
            }
            label="Enable staging webhooks"
          />
          <TextField
            fullWidth
            label="Webhook auth header name"
            value={form.webhookAuthHeaderName}
            size="small"
            onChange={(e) => {
              setField.webhookAuthHeaderName(e.target.value);
            }}
          />
          <TextField
            fullWidth
            label="Webhook auth header value"
            value={form.webhookAuthHeaderValue}
            size="small"
            type="password"
            onChange={(e) => {
              setField.webhookAuthHeaderValue(e.target.value);
            }}
          />
          <AdvancedApiKeyFields
            payloadShapes={form.payloadShapes}
            eventTypes={form.eventTypes}
            idFormat={form.idFormat}
            customMapping={form.customMapping}
            emitTerminalHandoffEvents={form.emitTerminalHandoffEvents}
            webhookTimestampMode={form.webhookTimestampMode}
            eventTypeOptions={eventTypeOptions}
            onWebhookTimestampModeChange={setField.webhookTimestampMode}
            onEmitTerminalHandoffEventsChange={
              setField.emitTerminalHandoffEvents
            }
            onPayloadShapesChange={setField.payloadShapes}
            onEventTypesChange={setField.eventTypes}
            onIdFormatChange={setField.idFormat}
            onCustomMappingChange={setField.customMapping}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={loading} onClick={handleClose}>
          Cancel
        </Button>
        <Button
          disabled={
            loading || form.name.trim().length === 0 || hasOutOfScopeEventTypes
          }
          variant="contained"
          onClick={handleSubmit}
        >
          {loading ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateApiKeyDialog;
